import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogin } from '../api/authApi';
import { useAuthStore } from '../store/authStore';
import { TrustFlightLogoIcon, MicrosoftLogoIcon } from '../assets/icons';

interface LocationState {
  from: {
    pathname: string;
  };
}

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState)?.from?.pathname || '/admin';
  
  const { login } = useAuthStore();
  const loginMutation = useLogin();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!showPassword) {
      setShowPassword(true);
      return;
    }
    
    try {
      const result = await loginMutation.mutateAsync({ email, password });
      login(result.user, result.token);
      
      // Wait for token to be persisted to localStorage before navigating
      // This prevents race conditions where ProtectedRoute checks auth before token is available
      let tokenPersisted = false;
      let attempts = 0;
      const maxAttempts = 10;
      
      while (attempts < maxAttempts) {
        try {
          const storageData = localStorage.getItem('auth-storage');
          if (storageData) {
            const parsed = JSON.parse(storageData);
            if (parsed?.state?.token) {
              tokenPersisted = true;
              break;
            }
          }
        } catch {
          // Ignore parse errors
        }
        await new Promise(resolve => setTimeout(resolve, 50));
        attempts++;
      }
      
      // Only navigate if token was successfully persisted
      if (tokenPersisted) {
        navigate(from, { replace: true });
      } else {
        console.error('Failed to persist token to localStorage after', attempts, 'attempts');
        setError('Login succeeded but failed to save session. Please try again.');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
    }
  };
  
  // Define inline styles to ensure consistency
  const styles = {
    container: {
      minHeight: '100%',
      height: '100%',
      width: '100%',
      backgroundColor: '#1A2233',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    card: {
      width: '100%',
      maxWidth: '400px',
      backgroundColor: '#1E2738',
      border: '1px solid #2E3B52',
      borderRadius: '8px',
      padding: '32px',
      margin: '0 16px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    logoContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      marginBottom: '32px',
    },
    logoBox: {
      width: '64px',
      height: '64px',
      backgroundColor: '#273142',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    title: {
      color: 'white',
      fontSize: '20px',
      fontWeight: 'normal',
    },
    errorMessage: {
      marginBottom: '24px',
      color: '#f87171',
      fontSize: '14px',
    },
    formGroup: {
      marginBottom: '24px',
    },
    label: {
      display: 'block',
      color: 'white',
      fontSize: '14px',
      marginBottom: '8px',
    },
    input: {
      width: '100%',
      backgroundColor: '#1A2233',
      border: '1px solid #2A3348',
      color: '#E5E7EB',
      borderRadius: '4px',
      padding: '10px 12px',
      fontSize: '14px',
      boxSizing: 'border-box' as const,
    },
    forgotPassword: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginBottom: '24px',
    },
    forgotPasswordLink: {
      fontSize: '14px',
      color: '#6C95E1',
      textDecoration: 'none',
    },
    dividerText: {
      textAlign: 'center' as const,
      fontSize: '14px',
      color: '#8A94A6',
      marginBottom: '16px',
    },
    microsoftButton: {
      width: '100%',
      backgroundColor: '#1A2233',
      border: '1px solid #2A3348',
      color: 'white',
      borderRadius: '4px',
      padding: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '24px',
      cursor: 'pointer',
    },
    microsoftIcon: {
      marginRight: '8px',
    },
    continueButton: {
      width: '100%',
      backgroundColor: '#0078D4',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '10px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    footer: {
      marginTop: '32px',
      textAlign: 'center' as const,
    },
    footerText: {
      fontSize: '14px',
      color: '#8A94A6',
    },
    footerLink: {
      color: 'white',
      textDecoration: 'none',
      fontSize: '14px',
    }
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <div style={styles.logoBox}>
            <TrustFlightLogoIcon width="32" height="32" stroke="white" />
          </div>
          <h1 style={styles.title}>Sign in to TrustFlight</h1>
        </div>
        
        {error && (
          <div style={styles.errorMessage}>
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              Email
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={styles.input}
              placeholder="Enter your email address"
            />
          </div>
          
          {showPassword && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={styles.input}
                  placeholder="Enter your password"
                />
              </div>
              
              <div style={styles.forgotPassword}>
                <a href="#" style={styles.forgotPasswordLink}>
                  Forgot password?
                </a>
              </div>
              
              <div style={styles.dividerText}>
                or sign in with
              </div>
              
              <button
                type="button"
                style={styles.microsoftButton}
              >
                <MicrosoftLogoIcon width="16" height="16" style={styles.microsoftIcon} />
                Microsoft
              </button>
            </>
          )}
          
          <button
            type="submit"
            disabled={loginMutation.isPending}
            style={styles.continueButton}
          >
            {loginMutation.isPending ? 'Signing in...' : 'Continue'}
          </button>
          
          <div style={styles.footer}>
            <span style={styles.footerText}>Don't have an account? </span>
            <a href="#" style={styles.footerLink}>Contact support</a>
          </div>
        </form>
      </div>
    </div>
  );
}; 